import { createContext, useContext } from 'react';
import { runInAction, makeObservable, action, computed, observable } from 'mobx';
import IUWProductsPage from 'store/modules/uwProducts/iUWProductsPage';
import UWProductsModule, { AttributeInfo } from 'store/modules/uwProducts/uwProductsModule';
import { ProductTemplate, ProductTemplateChecklistCard } from 'types/productServiceV2';
import { resetBenefitSummaries } from 'api/operations/proposalServiceV3';
import { RemoveAttributeModalStore } from './removeAttributeModalStore';

export default class RFQPlanAttributesPageStore extends IUWProductsPage {
  uwProductsModule: UWProductsModule;

  private _isLoading = false;
  private _templateId: string = '';
  private _productTemplate: ProductTemplate | null = null;
  private _requiredAttributes: AttributeInfo[] = [];
  private _customAttributes: AttributeInfo[] = [];

  public removeAttributeModalDelegate: RemoveAttributeModalStore;

  // Getters
  public get isLoading(): boolean {
    return this._isLoading;
  }

  protected get productTemplate(): ProductTemplate | null {
    return this._productTemplate;
  }

  public get requiredAttributes(): AttributeInfo[] {
    return this._requiredAttributes;
  }

  public get customAttributes(): AttributeInfo[] {
    return this._customAttributes;
  }

  public get isStepCompleted(): boolean {
    return this.uwProductsModule.isStepCompleted(ProductTemplateChecklistCard.PLAN_ATTRIBUTES);
  }

  public get isTemplateInProgress(): boolean {
    return this.uwProductsModule.isTemplateInProgress;
  }

  public get isStepConfigurable(): boolean {
    return this.isTemplateInProgress && !this.isStepCompleted;
  }

  public get showCustomAttributesFeature(): boolean {
    return this.uwProductsModule.rootStore.domainStoreV2.showCustomAttributesFeature;
  }

  public get attributesProgressStatus(): string {
    return this.uwProductsModule.attributesProgressStatus;
  }

  // Setters
  private setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  private setProductTemplate(template: ProductTemplate | null): void {
    this._productTemplate = template;
  }

  private setRequiredAttributes(attributesInfo: AttributeInfo[]): void {
    this._requiredAttributes = attributesInfo;
  }

  private setCustomAttributes(attributesInfo: AttributeInfo[]): void {
    this._customAttributes = attributesInfo;
  }

  // Api calls
  public async getInitialData(): Promise<void> {
    this.setIsLoading(true);
    // Common initial requests from module
    await this.uwProductsModule.getInitialData(this._templateId);

    runInAction(() => {
      // Set data from module
      this.setProductTemplate(this.uwProductsModule.productTemplate);
      this.setRequiredAttributes(this.uwProductsModule.requiredAttributes);
      this.setCustomAttributes(this.uwProductsModule.customAttributes);

      this.setIsLoading(false);
    });
  }

  public async completeStep(): Promise<void> {
    this.setIsLoading(true);
    await this.uwProductsModule.onCompleteStep(ProductTemplateChecklistCard.PLAN_ATTRIBUTES);
    runInAction(() => {
      this.setIsLoading(false);
    });
  }

  public async uncompleteStep(): Promise<void> {
    this.setIsLoading(true);
    await this.uwProductsModule.onUncompleteStep(ProductTemplateChecklistCard.PLAN_ATTRIBUTES);
    await runInAction(async () => {
      if (this.productTemplate?.id) {
        await resetBenefitSummaries(this.productTemplate.id);
      }
      this.setIsLoading(false);
    });
  }

  // Helpers
  public resetInitialData(): void {
    this.setProductTemplate(null);
    this.setRequiredAttributes([]);
    this.setCustomAttributes([]);
    this.removeAttributeModalDelegate.close();
  }

  public get areAllAttributesFilled(): boolean {
    const planAttributes = this.productTemplate?.formAnswers.planAttributes;
    return planAttributes
      ? this.uwProductsModule.requiredAttributes.every((domainAttribute) => planAttributes[domainAttribute.id])
      : false;
  }

  constructor(templateId: string) {
    super();
    this._templateId = templateId;

    this.uwProductsModule = new UWProductsModule();
    this.removeAttributeModalDelegate = new RemoveAttributeModalStore({
      templateId: this._templateId,
      updateProductTemplate: this.getInitialData.bind(this),
    });

    makeObservable<
      RFQPlanAttributesPageStore,
      // Private observable properties and actions
      | '_productTemplate'
      | '_templateId'
      | '_requiredAttributes'
      | '_customAttributes'
      | '_isLoading'
      | 'setRequiredAttributes'
      | 'setCustomAttributes'
      | 'setIsLoading'
      | 'setProductTemplate'
    >(this, {
      // Private properties and actions
      _productTemplate: observable,
      _templateId: observable,
      _requiredAttributes: observable,
      _customAttributes: observable,
      _isLoading: observable,

      setRequiredAttributes: action.bound,
      setCustomAttributes: action.bound,
      setIsLoading: action.bound,
      setProductTemplate: action.bound,
      // Getters
      isLoading: computed,
      areAllAttributesFilled: computed,
      attributesProgressStatus: computed,
      isStepCompleted: computed,
      isTemplateInProgress: computed,
      isStepConfigurable: computed,
      requiredAttributes: computed,
      customAttributes: computed,

      // Other actions
      getInitialData: action.bound,
      completeStep: action.bound,
      uncompleteStep: action.bound,
      resetInitialData: action.bound,
    });
  }
}

const PageStoreContext = createContext(new RFQPlanAttributesPageStore('stub-template-id'));

export const useRFQPlanAttributesPageStore = (): RFQPlanAttributesPageStore => {
  return useContext(PageStoreContext);
};

export { PageStoreContext };
